import { formatDate, titleCase, type StatusLevel } from '@westhetics/ui'
import type {
  Question,
  QuestionType,
  QuestionnaireSection,
  QuestionnaireVersion,
  SectionKey,
} from './api'

/**
 * Como o questionário é escrito nas telas de quem o edita (docs/areas/anamnese.md, "Telas ·
 * Questionário"; docs/styleguide/04-escrita.md): tipo por extenso, a linha de apoio de cada
 * pergunta, o nome e o estado de cada versão. Nada aqui é regra; é escrita.
 */

export const TYPE_LABELS: Record<QuestionType, string> = {
  text: 'texto',
  yes_no: 'sim ou não',
  single_choice: 'escolha única',
  multiple_choice: 'escolha múltipla',
  scale: 'escala',
  date: 'data',
  number: 'número',
}

/** Os nomes dos quatro blocos do desenho, para quando a versão ainda não os tem. */
export const SECTION_NAMES: Record<SectionKey, string> = {
  aboutYou: 'Sobre você',
  yourHealth: 'Sua saúde',
  yourExpectations: 'O que você espera',
  selfImage: 'Como você se vê hoje',
}

/** O que a paciente confirma no bloco 1: são os campos do cadastro, não perguntas (RN-ANA-06). */
export const ABOUT_YOU_NOTE =
  'Sem perguntas: a paciente confirma os dados do cadastro — nome, data de nascimento, CPF, profissão, e-mail, WhatsApp, endereço e estado civil.'

/** O bloco 5 é fixo: resumo, declaração com aceite e envio (RN-ANA-13). */
export const CONFIRMATION_NOTE =
  'Resumo das respostas por bloco, a declaração com a caixa "Li e concordo com esta declaração", a frase "Registramos data, hora e dispositivo do envio. Uma cópia vai para o seu e-mail." e o botão "Enviar minha ficha".'

export const YES_NO_LABELS: Record<string, string> = {
  yes: 'Sim',
  no: 'Não',
  unsure: 'Não sei dizer',
}

/** O que basta para listar as respostas de uma pergunta: serve à da API e ao rascunho da tela. */
export interface AnswerSource {
  type: QuestionType
  allowsUnsure: boolean
  options: readonly { value: string; label: string }[]
}

/** As respostas que uma pergunta aceita, com o rótulo de cada uma, para condição e leitura. */
export function answerChoices(question: AnswerSource): { value: string; label: string }[] {
  if (question.type === 'yes_no') {
    const values = question.allowsUnsure ? ['yes', 'no', 'unsure'] : ['yes', 'no']
    return values.map((value) => ({ value, label: YES_NO_LABELS[value]! }))
  }
  return question.options.map((option) => ({ value: option.value, label: option.label }))
}

/** "Sim · Não · Não sei dizer" ou os rótulos das opções; vazio em texto, data e número. */
export function optionsLine(question: AnswerSource) {
  return answerChoices(question)
    .map((choice) => choice.label)
    .join(' · ')
}

/** "aparece se 'Você usa algum medicamento contínuo?' = Sim" (RN-ANA-11). */
export function conditionLine(
  question: Pick<Question, 'condition'>,
  siblings: readonly (AnswerSource & Pick<Question, 'key' | 'label'>)[],
) {
  if (!question.condition) return null
  const source = siblings.find((sibling) => sibling.key === question.condition?.questionKey)
  const choices = source ? answerChoices(source) : []
  const answers = question.condition.values
    .map((value) => choices.find((choice) => choice.value === value)?.label ?? value)
    .join(' ou ')
  const label = source ? `"${source.label}"` : question.condition.questionKey
  return `aparece se ${label} = ${answers}`
}

/** A pergunta sinaliza o bloco de atenção, pela regra dela ou por alguma opção (RN-ANA-20). */
export function raisesAttention(question: Pick<Question, 'flagRule' | 'options'>) {
  return Boolean(question.flagRule) || question.options.some((option) => option.raisesFlag)
}

/**
 * A linha de apoio da pergunta em leitura: tipo, opções, obrigatoriedade, condição, descreva e
 * atenção, por extenso e separados por " · ". Nada abreviado, nada em Plum.
 */
export function questionMetaLine(
  question: Question,
  siblings: readonly Question[],
  options: { showReview?: boolean } = {},
) {
  const parts: string[] = [TYPE_LABELS[question.type]]
  const choices = optionsLine(question)
  if (choices) parts.push(choices)
  if (!question.isRequired) parts.push('opcional')
  if (question.isSkippable) parts.push('pode pular')
  if (question.isSensitive) parts.push('sensível')
  const condition = conditionLine(question, siblings)
  if (condition) parts.push(condition)
  if (question.followUp) parts.push(`descreva: "${question.followUp.label}"`)
  if (question.example) parts.push(`dica: "${question.example}"`)
  if (raisesAttention(question)) parts.push('pede atenção da profissional')
  if (options.showReview) {
    const missing = [
      ...(question.review.legal ? [] : ['jurídica']),
      ...(question.review.clinical ? [] : ['clínica']),
    ]
    parts.push(missing.length === 0 ? 'revisada' : `falta revisão ${missing.join(' e ')}`)
  }
  return parts.join(' · ')
}

/** "1 · SOBRE VOCÊ": o rótulo do bloco, pela posição no questionário. */
export function sectionEyebrow(section: Pick<QuestionnaireSection, 'name'>, index: number) {
  return `${index + 1} · ${section.name}`
}

/** "Versão 3" ou "Rascunho". */
export function versionTitle(version: Pick<QuestionnaireVersion, 'status' | 'number'>) {
  return version.status === 'draft' ? 'Rascunho' : `Versão ${version.number ?? ''}`.trim()
}

/** Vigente é o trabalho feito; rascunho depende de ação; anterior só existe para leitura. */
export function versionStatus(version: Pick<QuestionnaireVersion, 'status' | 'isCurrent'>): {
  label: string
  level: StatusLevel
} {
  if (version.status === 'draft') return { label: 'Rascunho', level: 'attention' }
  if (version.isCurrent) return { label: 'Vigente', level: 'done' }
  return { label: 'Anterior', level: 'waiting' }
}

/** "Publicada em 01/09/2026 por Ana Westhetics" · "Criado em 28/09 por Ana Westhetics". */
export function versionPublicationLine(version: QuestionnaireVersion) {
  if (version.status === 'published' && version.publishedAt) {
    const by = version.publishedBy ? ` por ${titleCase(version.publishedBy.name)}` : ''
    return `Publicada em ${formatDate(new Date(version.publishedAt), { year: true })}${by}`
  }
  const by = version.createdBy ? ` por ${titleCase(version.createdBy.name)}` : ''
  return `Criado em ${formatDate(new Date(version.createdAt), { year: false })}${by}`
}

/** "18 perguntas" · "1 pergunta". */
export function questionCountLine(count: number) {
  return `${count} ${count === 1 ? 'pergunta' : 'perguntas'}`
}

const SECTION_PATH = /^(?:sections|questions)\.([a-zA-Z]+)(?:\.(?:questions\.)?([a-zA-Z0-9]+))?/

/**
 * Um aviso ou erro de publicação da API, por extenso: "questions.yourHealth.homeAcidUse: falta
 * revisão clínica" vira "Sua saúde · "Usa ácido ou algum tratamento de pele em casa?": falta revisão
 * clínica"; "sections.yourExpectations: falta pelo menos uma pergunta" vira "O que você espera:
 * falta pelo menos uma pergunta".
 */
export function publishIssueSentence(
  issue: string,
  sections: readonly Pick<QuestionnaireSection, 'key' | 'name' | 'questions'>[],
) {
  const [path, ...rest] = issue.split(':')
  const message = rest.join(':').trim()
  const match = SECTION_PATH.exec(path ?? '')
  if (!match) return issue
  const [, sectionKey, questionKey] = match
  const section = sections.find((s) => s.key === sectionKey)
  const sectionName = section?.name ?? SECTION_NAMES[sectionKey as SectionKey] ?? sectionKey
  const question =
    questionKey && questionKey !== 'questions'
      ? section?.questions.find((q) => q.key === questionKey)
      : undefined
  const where = question ? `${sectionName} · "${question.label}"` : sectionName
  return message ? `${where}: ${message}` : where
}
